import { UploadingType } from '~/client/graph/types.generated'
import {
  capturedCssWidth,
  copyMapFrame,
  frameDensity,
  freezeMapFrame,
  nextFrame,
} from '~/client/src/shared/components/MapBoxViewer/copyMapFrame'
import { drawMapCredits } from '~/client/src/shared/components/MapBoxViewer/drawMapCredits'
import {
  suspendMapAnimations,
  waitForMapFrame,
} from '~/client/src/shared/map/mapCaptureFrame'
import { spanForZoom, zoomForSpan } from '~/client/src/shared/map/mapGroundSpan'
import { PROJECT_MARKER_ZOOM_LEVEL } from '~/client/src/shared/map/mapZoomRule'
import { PIN_SPEC } from '~/client/src/shared/map/markerGeometry'
import { FileUploadingStore } from '~/client/src/shared/stores/domain/FileUploading.store'

/** Width/height used when cropping a full-screen map capture to match the
 *  collapsed mini-map field dimensions (matches COLLAPSED_MAP_HEIGHT). */
export const CAPTURE_HEIGHT = 120
export const CAPTURE_WIDTH = 400

/** Site scale. In metres, not levels: a fixed delta covers 1.55x more ground at the equator. */
export const OVERVIEW_ANCHOR_SPAN_M = 600

/** Closer than this and the two images read as one picture twice. */
export const OVERVIEW_MIN_ZOOM_OUT = 1.5

export const OVERVIEW_MAX_ZOOM_OUT = 3

/** Sub-pixel zoom drift below which a re-render isn't worth waiting for. */
const ZOOM_EPSILON = 0.01

export const CAPTURE_FAILED_MESSAGE =
  'Failed to capture location map screenshot. The PDF report may not include the map image.'

/** For both shots together, so a slow network costs one budget rather than two. */
export const CAPTURE_FRAME_BUDGET_MS = 12000

interface CaptureParams {
  map: mapboxgl.Map
  fileUploadingStore: FileUploadingStore
  projectId: string
  /** When set, navigate to this viewport before capturing, then restore. */
  preciseViewport?: {
    latitude: number
    longitude: number
    zoom: number
    bearing?: number
    pitch?: number
  }
  /** When set, center-crop each captured canvas to this size. */
  cropSize?: { width: number; height: number }
  /** Satellite imagery carries a third rightsholder, and the stamped credits have to name it. */
  isSatellite?: boolean
  /** Fires once the camera is back, well before the uploads finish. */
  onCameraRestored?: () => void
}

/**
 * Captures precise + overview screenshots from a Mapbox GL map, uploads them,
 * and returns the URLs. Shared by WorkflowFormPreciseLocation (mini-map) and
 * WorkflowComposerStore (logistics map).
 */
export async function captureMapScreenshots(
  params: CaptureParams,
): Promise<{ imageUrl?: string; overviewImageUrl?: string }> {
  const {
    map,
    fileUploadingStore,
    projectId,
    preciseViewport,
    cropSize,
    isSatellite,
    onCameraRestored,
  } = params

  try {
    // Save original viewport so we can restore it in finally
    const savedCenter = map.getCenter()
    const savedZoom = map.getZoom()
    const savedBearing = map.getBearing()
    const savedPitch = map.getPitch()
    let viewportChanged = false

    // The camera moves must never reach the screen: this runs on submit, on a map the user is
    // watching. One frame first — submit swaps the composer's pin to GL, and the still has to be
    // the map as it looks after that swap.
    await nextFrame()
    const unfreeze = freezeMapFrame(map)
    const resume = suspendMapAnimations(map)
    const deadline = Date.now() + CAPTURE_FRAME_BUDGET_MS
    // An unfinished frame is never shot: a half-loaded image in a report reads as a real one.
    const isFrameComplete = (): Promise<boolean> =>
      waitForMapFrame(map, Math.max(0, deadline - Date.now()))

    let preciseCanvas: HTMLCanvasElement
    let overviewCanvas: HTMLCanvasElement

    try {
      if (preciseViewport) {
        map.jumpTo({
          center: [preciseViewport.longitude, preciseViewport.latitude],
          zoom: preciseViewport.zoom,
          bearing: preciseViewport.bearing ?? 0,
          pitch: preciseViewport.pitch ?? 0,
        })
        viewportChanged = true
      }
      if (!(await isFrameComplete())) {
        throw new Error('precise frame did not complete')
      }
      preciseCanvas = captureRegion(map, cropSize, isSatellite)

      const overviewZoom = overviewZoomFor(map, cropSize)
      // Directional: at the floor there is no wider shot to take, so none is rendered.
      if (map.getZoom() - overviewZoom > ZOOM_EPSILON) {
        map.setZoom(overviewZoom)
        viewportChanged = true
        // A missed overview leaves the finished precise shot to stand in for it, as a failed
        // upload does, rather than discarding both.
        if (await isFrameComplete()) {
          overviewCanvas = captureRegion(map, cropSize, isSatellite)
        }
      }
    } finally {
      try {
        // Pixels are in hand, so the camera goes back before the uploads rather than after them.
        if (viewportChanged) {
          map.jumpTo({
            center: savedCenter,
            zoom: savedZoom,
            bearing: savedBearing,
            pitch: savedPitch,
          })
        }
      } finally {
        resume()
        unfreeze()
        onCameraRestored?.()
      }
    }

    // Each side fails on its own; mergeSnapshotResult keeps whichever image survived.
    const upload = async (
      canvas: HTMLCanvasElement,
      name: string,
    ): Promise<string | undefined> => {
      if (!canvas) return undefined
      try {
        const blob = await new Promise<Blob | null>(resolve =>
          canvas.toBlob(b => resolve(b), 'image/png'),
        )
        if (!blob) return undefined
        const file = new File([blob], name, { type: 'image/png' })
        const [result] = await fileUploadingStore.uploadFile(
          file,
          UploadingType.Image,
          projectId,
          file.name,
          true,
        )
        return result?.fileURL
      } catch {
        return undefined
      }
    }

    const [imageUrl, overviewImageUrl] = await Promise.all([
      upload(preciseCanvas, 'precise-location-map.png'),
      upload(overviewCanvas, 'precise-location-overview.png'),
    ])

    // One upload serves both when no wider shot was taken or it did not complete.
    return { imageUrl, overviewImageUrl: overviewImageUrl ?? imageUrl }
  } catch {
    return {}
  }
}

function overviewZoomFor(
  map: mapboxgl.Map,
  cropSize?: { width: number; height: number },
): number {
  const { lat } = map.getCenter()
  const widthPx = capturedCssWidth(map, cropSize)
  const preciseSpan = spanForZoom(map.getZoom(), lat, widthPx)
  const span = Math.max(
    preciseSpan * 2 ** OVERVIEW_MIN_ZOOM_OUT,
    Math.min(OVERVIEW_ANCHOR_SPAN_M, preciseSpan * 2 ** OVERVIEW_MAX_ZOOM_OUT),
  )
  // Floored above the band where every marker gives way to the project pin.
  return Math.max(
    zoomForSpan(span, lat, widthPx),
    PROJECT_MARKER_ZOOM_LEVEL + 1,
  )
}

/** The map's own pixels, plus the credits its DOM controls carry and a GL read cannot. */
function captureRegion(
  map: mapboxgl.Map,
  cropSize?: { width: number; height: number },
  isSatellite?: boolean,
): HTMLCanvasElement {
  // The camera centres on the coordinate, but the pin is anchored by its tip and reaches upward,
  // so a centred crop cuts its head off. Lift the crop by half a pin to frame the pin, not the tip.
  const canvas = copyMapFrame(map, { cropSize, liftPx: PIN_SPEC.boxPx / 2 })
  if (!canvas) {
    return null
  }
  drawMapCredits(
    canvas.getContext('2d'),
    canvas.width,
    canvas.height,
    frameDensity(map),
    isSatellite,
  )
  return canvas
}
